import React from "react";
import PropTypes from "prop-types";
import { ATTENDANCE_DECISIONS } from "./attendanceDraft";

const getInitials = student => (
  `${(student.firstName || "").charAt(0)}${(student.lastName || "").charAt(0)}`.toUpperCase()
);

const getStudentName = student => (
  `${student.firstName || ""} ${student.lastName || ""}`.trim() || student.id
);

const formatNoticeUnit = (value, singular, plural) => (
  value === 1 ? `${value} ${singular}` : `${value} ${plural}`
);

const formatNoticeDuration = (noticeMinutes) => {
  if (noticeMinutes === null || noticeMinutes === undefined || Number.isNaN(noticeMinutes)) {
    return "";
  }

  if (noticeMinutes < 1) {
    return "mniej niż minutę przed lekcją";
  }

  const days = Math.floor(noticeMinutes / (24 * 60));
  const hours = Math.floor((noticeMinutes % (24 * 60)) / 60);
  const minutes = noticeMinutes % 60;
  const parts = [];

  if (days) parts.push(formatNoticeUnit(days, "dzień", "dni"));
  if (hours) parts.push(`${hours} godz.`);
  if (minutes) parts.push(`${minutes} min`);

  return `${parts.slice(0, 2).join(" ")} przed lekcją`;
};

const getNoticeMinutes = (decision, lessonDetails) => {
  const cancellation = decision.cancellation || {};

  if (cancellation.reportedAt && lessonDetails.date && lessonDetails.startTime) {
    const lessonStart = new Date(`${lessonDetails.date}T${lessonDetails.startTime}`);
    const reportedAt = new Date(cancellation.reportedAt);

    if (!Number.isNaN(lessonStart.getTime()) && !Number.isNaN(reportedAt.getTime())) {
      return Math.max(0, Math.round((lessonStart.getTime() - reportedAt.getTime()) / 60000));
    }
  }

  if (cancellation.cancellationNoticeHours !== undefined && cancellation.cancellationNoticeHours !== null) {
    return Number(cancellation.cancellationNoticeHours) * 60;
  }

  return null;
};

const getCancellationLabel = (decision, lessonDetails) => {
  const notice = formatNoticeDuration(getNoticeMinutes(decision, lessonDetails));
  const suffix = notice ? ` · ${notice}` : "";

  if (decision.decision === ATTENDANCE_DECISIONS.CANCELLED_ON_TIME) {
    return `Odwołano w terminie${suffix}`;
  }

  if (decision.decision === ATTENDANCE_DECISIONS.CANCELLED_LATE) {
    return `Odwołano po terminie${suffix}`;
  }

  return "";
};

const isCancelledDecision = decision => (
  decision.decision === ATTENDANCE_DECISIONS.CANCELLED_ON_TIME ||
  decision.decision === ATTENDANCE_DECISIONS.CANCELLED_LATE
);

function AttendanceDecisionRow({
  student,
  decision,
  invalid,
  lessonDetails,
  canEdit,
  onDecisionChange,
}) {
  const cancellationLabel = getCancellationLabel(decision, lessonDetails);
  const studentName = getStudentName(student);
  const cancelled = isCancelledDecision(decision);

  return (
    <div className={`attendance-page__student-row ${invalid ? "attendance-page__student-row--invalid" : ""}`}>
      <div className="attendance-page__avatar">{getInitials(student)}</div>
      <div className="attendance-page__student-main">
        <div className="attendance-page__student-name">{studentName}</div>
        {student.age && <div className="attendance-page__student-meta">{student.age} lat</div>}
      </div>
      <div className="attendance-page__segments" role="group" aria-label={`Obecność: ${studentName}`}>
        <button
          type="button"
          className={`attendance-page__segment ${decision.decision === ATTENDANCE_DECISIONS.PRESENT ? "attendance-page__segment--selected attendance-page__segment--success" : ""}`}
          disabled={!canEdit}
          onClick={() => onDecisionChange(student.id, ATTENDANCE_DECISIONS.PRESENT)}
        >
          Obecny
        </button>
        <button
          type="button"
          className={`attendance-page__segment ${decision.decision === ATTENDANCE_DECISIONS.ABSENT ? "attendance-page__segment--selected attendance-page__segment--danger" : ""}`}
          disabled={!canEdit || cancelled}
          onClick={() => onDecisionChange(student.id, ATTENDANCE_DECISIONS.ABSENT)}
        >
          Nieobecny
        </button>
        <button
          type="button"
          className={`attendance-page__segment ${isCancelledDecision(decision) ? "attendance-page__segment--selected attendance-page__segment--warning" : ""}`}
          disabled
          title="Odwołanie dodaj przez Akcje lekcji."
        >
          Odwołał
        </button>
      </div>
      {cancellationLabel && (
        <div className="attendance-page__badge">{cancellationLabel}</div>
      )}
    </div>
  );
}

AttendanceDecisionRow.propTypes = {
  student: PropTypes.object.isRequired,
  decision: PropTypes.object.isRequired,
  invalid: PropTypes.bool,
  lessonDetails: PropTypes.object.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onDecisionChange: PropTypes.func.isRequired,
};

AttendanceDecisionRow.defaultProps = {
  invalid: false,
};

export default AttendanceDecisionRow;
